import { useEffect, useRef, useState } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * A number that runs up to its value, the way the dashboard's counters do
 * (theme «Графит»). Starts from zero on mount, then moves from the previous
 * value when it changes. Whole numbers only; lands exactly on `value`.
 * With prefers-reduced-motion the value is shown at once.
 */
export const useCountUp = (value: number, duration = 700) => {
  const [shown, setShown] = useState(() =>
    prefersReducedMotion() ? value : 0,
  );
  const current = useRef(shown);

  useEffect(() => {
    if (prefersReducedMotion() || duration <= 0) {
      current.current = value;
      setShown(value);
      return;
    }
    const from = current.current;
    if (from === value) return;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      const next = t === 1 ? value : Math.round(from + (value - from) * eased);
      current.current = next;
      setShown(next);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return shown;
};
